/*
 * Books' accounts, rules and categories (the chart of accounts). Rules can ask for money in or out only and one account
 * only, and can be changed in place, like QuickBooks' bank rules.
 */
import { useEffect, useState } from "react";
import { CATEGORY_GROUPS, type BankAccountRow, type BooksSetup, type RuleRow } from "@steward/shared";
import {
  useQuickBooksChart,
  useAddCategory,
  useAddRule,
  usePlaidSync,
  useChangeRule,
  useHideCategory,
  useRemoveAccount,
  useRemoveRule,
  useSaveAccount,
} from "../../api/books";
import { DataTable } from "../../components/DataTable";
import { Choice, Combobox, InputText } from "../../components/fields";
import { moneyExact, shortDate } from "../../components/format";
import { Modal, useToast } from "../../components/overlays";
import { Button, Card, EmptyState, IconButton, StatusLabel, Switch } from "../../components/parts";
import { KIND_WORDS, StartDayPick, centsOf, errText, pickList } from "./common";
import { useSession } from "../../app/session";
import { useSettings, useUpdateSettings } from "../../api/hooks";

export function Setup({ setup }: { setup: BooksSetup }) {
  const [edit, setEdit] = useState<BankAccountRow | "new" | null>(null);
  const [rule, setRule] = useState<RuleRow | "new" | null>(null);
  const removeRule = useRemoveRule();
  const toast = useToast();
  const cats = new Map(setup.categories.map((c) => [c.id, c.name]));
  const accts = new Map(setup.accounts.map((a) => [a.id, a.name]));
  return (
    <div className="stack">
      <StartsOn />
      <Card
        title="Bank, card and cash accounts"
        flush
        action={
          <Button size="small" icon="plus" onClick={() => setEdit("new")}>
            Add an account
          </Button>
        }
      >
        <DataTable
          title="Accounts"
          rows={setup.accounts}
          rowKey={(a) => a.id}
          onRowClick={setEdit}
          empty={
            <EmptyState
              icon="building"
              title="No accounts yet"
              action={<Button onClick={() => setEdit("new")}>Add an account</Button>}
            >
              Add each bank account and credit card the business uses, and a Cash account for money paid by hand.
            </EmptyState>
          }
          columns={[
            { key: "n", header: "Account", render: (a) => <b>{a.name}</b> },
            {
              key: "k",
              header: "What it is",
              render: (a) => (
                <span>
                  {KIND_WORDS[a.kind] ?? a.kind}
                  {a.last4 ? ` ending ${a.last4}` : ""}
                  {a.personal ? (
                    <>
                      {" "}
                      <StatusLabel status="warning">Personal</StatusLabel>
                    </>
                  ) : null}
                </span>
              ),
            },
            {
              key: "o",
              header: "Opening balance",
              align: "right",
              render: (a) =>
                a.openingDay ? (
                  <span className="num">
                    {moneyExact(a.openingBalanceCents)} <span className="muted small">on {shortDate(a.openingDay)}</span>
                  </span>
                ) : a.kind === "cash" ? (
                  ""
                ) : (
                  <StatusLabel status="warning">Not set</StatusLabel>
                ),
            },
            {
              key: "l",
              header: "",
              render: (a) => (a.linked ? <StatusLabel status="informative">Linked bank</StatusLabel> : null),
            },
            {
              key: "r",
              header: "Reconciled through",
              render: (a) => (a.kind === "cash" ? "" : a.reconciledThrough ? shortDate(a.reconciledThrough) : "Not yet"),
            },
          ]}
        />
      </Card>

      <Card
        title="Rules"
        flush
        action={
          <Button size="small" icon="plus" type="secondary" onClick={() => setRule("new")}>
            Add a rule
          </Button>
        }
      >
        <p className="muted bk-pad">
          When the bank's words have these letters, the category is set on its own. The newest rule that fits wins.
        </p>
        <DataTable
          title="Rules"
          rows={setup.rules}
          rowKey={(r) => r.id}
          onRowClick={setRule}
          empty={
            <EmptyState icon="tag" title="No rules yet">
              Add one here, or from any transaction.
            </EmptyState>
          }
          columns={[
            { key: "m", header: "When the bank says", render: (r) => <b>{r.match}</b> },
            {
              key: "o",
              header: "For",
              render: (r) =>
                [
                  r.direction === "in" ? "Money in" : r.direction === "out" ? "Money out" : "In or out",
                  r.accountId ? accts.get(r.accountId) : null,
                ]
                  .filter(Boolean)
                  .join(", "),
            },
            { key: "c", header: "Category", render: (r) => cats.get(r.categoryId) ?? "" },
            { key: "p", header: "Paid to", render: (r) => r.payee ?? "" },
            {
              key: "x",
              header: "",
              align: "right",
              render: (r) => (
                <IconButton
                  icon="trash"
                  label={`Remove the rule for ${r.match}`}
                  onClick={() => void removeRule.mutateAsync(r.id).then(() => toast("Rule removed"))}
                />
              ),
            },
          ]}
        />
      </Card>

      <Categories setup={setup} />
      {edit ? <AccountModal account={edit === "new" ? null : edit} onClose={() => setEdit(null)} /> : null}
      {rule ? <RuleModal setup={setup} rule={rule === "new" ? null : rule} onClose={() => setRule(null)} /> : null}
    </div>
  );
}

function Categories({ setup }: { setup: BooksSetup }) {
  const [newCat, setNewCat] = useState("");
  const [newType, setNewType] = useState<"expense" | "income">("expense");
  const [parent, setParent] = useState("");
  const addCat = useAddCategory();
  const hide = useHideCategory();
  const qb = useQuickBooksChart();
  const toast = useToast();
  const cats = new Map(setup.categories.map((c) => [c.id, c.name]));
  return (
    <Card
      title="Categories"
      action={
        <Button
          size="small"
          type="secondary"
          loading={qb.isPending}
          onClick={() =>
            qb.mutate(undefined, {
              onSuccess: (r) =>
                toast(
                  r.renamed || r.added
                    ? `Renamed ${String(r.renamed)} and added ${String(r.added)} to match QuickBooks.`
                    : "Your categories already match the QuickBooks list.",
                ),
              onError: (e) => toast(errText(e), { kind: "error" }),
            })
          }
        >
          Use the QuickBooks list
        </Button>
      }
    >
      <p className="muted">
        Each one has its Schedule C line for the accountant. Hide ones you never use. A hidden one with transactions still counts.
      </p>
      <div className="form-grid">
        <InputText label="New category" value={newCat} maxLength={40} onChange={(e) => setNewCat(e.target.value)} />
        <Choice
          label="Kind"
          value={newType}
          onChange={setNewType}
          options={[
            { value: "expense", label: "Cost" },
            { value: "income", label: "Income" },
          ]}
        />
        <Combobox
          label="Inside"
          value={parent}
          onChange={setParent}
          options={[
            { value: "", label: "Its own category" },
            ...setup.categories.filter((c) => !c.parentId && c.type === newType).map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
      </div>
      <Button
        size="small"
        disabled={!newCat.trim()}
        loading={addCat.isPending}
        onClick={() =>
          void addCat.mutateAsync({ name: newCat.trim(), type: newType, ...(parent ? { parentId: parent } : {}) }).then(
            () => {
              setNewCat("");
              toast("Category added");
            },
            (e: unknown) => toast(errText(e), { kind: "error" }),
          )
        }
      >
        Add category
      </Button>
      <div className="bk-cat-groups">
        {Object.entries(CATEGORY_GROUPS).flatMap(([, groups]) =>
          groups.map((g) => {
            const inGroup = setup.categories.filter((c) => c.group === g);
            if (inGroup.length === 0) return null;
            return (
              <section key={g} className="bk-cat-group">
                <h3>{g}</h3>
                {inGroup.map((c) => (
                  <div className="row between bk-cat-line" key={c.id}>
                    <span className={c.hidden ? "muted" : undefined}>
                      {c.parentId ? `${cats.get(c.parentId) ?? ""}, ` : ""}
                      {c.name} {c.taxLine ? <span className="muted small">line {c.taxLine}</span> : null}
                    </span>
                    <Switch
                      checked={!c.hidden}
                      onChange={(on) => void hide.mutateAsync({ id: c.id, hidden: !on })}
                      label={`Show ${c.name}`}
                    />
                  </div>
                ))}
              </section>
            );
          }),
        )}
      </div>
    </Card>
  );
}

function RuleModal({ setup, rule, onClose }: { setup: BooksSetup; rule: RuleRow | null; onClose: () => void }) {
  const add = useAddRule();
  const change = useChangeRule();
  const toast = useToast();
  const [match, setMatch] = useState(rule?.match ?? "");
  const [categoryId, setCategoryId] = useState(rule?.categoryId ?? "");
  const [payee, setPayee] = useState(rule?.payee ?? "");
  const [direction, setDirection] = useState<"any" | "in" | "out">(rule?.direction ?? "any");
  const [accountId, setAccountId] = useState(rule?.accountId ?? "");
  const go = async () => {
    const body = {
      match: match.trim(),
      categoryId,
      ...(payee.trim() ? { payee: payee.trim() } : {}),
      direction: direction === "any" ? null : direction,
      accountId: accountId || null,
    };
    try {
      const r = rule ? await change.mutateAsync({ id: rule.id, ...body }) : await add.mutateAsync(body);
      toast(r.applied ? `Saved. It set ${String(r.applied)} that had no category.` : "Saved");
      onClose();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  return (
    <Modal
      title={rule ? "Change a rule" : "Add a rule"}
      open
      onClose={onClose}
      footer={
        <Button
          onClick={() => void go()}
          loading={add.isPending || change.isPending}
          disabled={match.trim().length < 3 || !categoryId}
        >
          Save
        </Button>
      }
    >
      <div className="stack">
        <InputText
          label="When the bank says"
          description="At least 3 letters, like SHELL or HOME DEPOT"
          value={match}
          maxLength={60}
          onChange={(e) => setMatch(e.target.value)}
        />
        <Choice
          label="For"
          value={direction}
          onChange={setDirection}
          options={[
            { value: "out", label: "Money out" },
            { value: "in", label: "Money in" },
            { value: "any", label: "Either" },
          ]}
        />
        <Combobox
          label="Account"
          value={accountId}
          onChange={setAccountId}
          options={[{ value: "", label: "Any account" }, ...setup.accounts.map((a) => ({ value: a.id, label: a.name }))]}
        />
        <Combobox label="Category" value={categoryId} onChange={setCategoryId} options={pickList(setup.categories)} />
        <InputText
          label="Paid to"
          description="Optional. For contractors on 1099s"
          value={payee}
          maxLength={60}
          onChange={(e) => setPayee(e.target.value)}
        />
      </div>
    </Modal>
  );
}

/** The first day in Books. Saving pulls the banks again, so older lines leave and opening balances follow. */
function StartsOn() {
  const { company } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const sync = usePlaidSync();
  const toast = useToast();
  const saved = s.data?.settings.books.startDay;
  const [start, setStart] = useState("");
  useEffect(() => {
    if (saved !== undefined) setStart(saved);
  }, [saved]);
  if (saved === undefined) return null;
  const go = async () => {
    try {
      await save.mutateAsync({ books: { startDay: start } });
      await sync.mutateAsync();
      toast("Saved. Books now starts there.");
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  return (
    <Card title="Books starts on">
      <div className="stack">
        <StartDayPick key={saved} value={start} onChange={setStart} />
        <div>
          <Button
            onClick={() => void go()}
            loading={save.isPending || sync.isPending}
            disabled={start === saved || (start !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(start))}
          >
            Save
          </Button>
        </div>
      </div>
    </Card>
  );
}

function AccountModal({ account, onClose }: { account: BankAccountRow | null; onClose: () => void }) {
  const save = useSaveAccount();
  const remove = useRemoveAccount();
  const toast = useToast();
  const [name, setName] = useState(account?.name ?? "");
  const [kind, setKind] = useState<BankAccountRow["kind"]>(account?.kind ?? "checking");
  const [personal, setPersonal] = useState(account?.personal ?? false);
  const [last4, setLast4] = useState(account?.last4 ?? "");
  const [opening, setOpening] = useState(account ? (account.openingBalanceCents / 100).toFixed(2) : "");
  const [day, setDay] = useState(account?.openingDay ?? "");
  const go = async () => {
    try {
      await save.mutateAsync({
        ...(account ? { id: account.id } : {}),
        name: name.trim(),
        kind,
        personal,
        last4: /^\d{4}$/.test(last4) ? last4 : null,
        openingBalanceCents: centsOf(opening || "0") ?? 0,
        openingDay: day || null,
      });
      toast("Saved");
      onClose();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  // Another business on the same bank login (one Chase sign in for two companies): its lines leave these books.
  const drop = async () => {
    if (!account) return;
    if (
      !confirm(
        `Remove ${account.name} from these books? Its lines come out of Books. The bank link stays for the other accounts.`,
      )
    )
      return;
    try {
      await remove.mutateAsync(account.id);
      toast("Account removed");
      onClose();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  return (
    <Modal
      title={account ? account.name : "Add an account"}
      open
      onClose={onClose}
      footer={
        <>
          {account ? (
            <Button type="tertiary" variation="destructive" onClick={() => void drop()} loading={remove.isPending}>
              Remove account
            </Button>
          ) : null}
          <Button onClick={() => void go()} loading={save.isPending} disabled={!name.trim()}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <InputText
          label="Name"
          placeholder="Chase checking"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
        />
        <Choice
          label="What it is"
          value={kind}
          onChange={setKind}
          options={[
            { value: "checking", label: "Checking" },
            { value: "savings", label: "Savings" },
            { value: "card", label: "Credit card" },
            { value: "credit", label: "Line of credit", hint: "Borrow and pay back as you go" },
            { value: "loan", label: "Loan", hint: "A set amount paid back each month" },
            { value: "cash", label: "Cash", hint: "For money paid or taken by hand" },
          ]}
        />
        <Choice
          label="Whose it is"
          value={personal ? "owner" : "business"}
          onChange={(v) => setPersonal(v === "owner")}
          options={[
            { value: "business", label: "The business" },
            {
              value: "owner",
              label: "Mine (personal)",
              hint: "Only business costs paid from it count. Money to or from it is a draw or money you put in.",
            },
          ]}
        />
        {kind !== "cash" ? (
          <InputText
            label="Last 4 digits"
            inputMode="numeric"
            maxLength={4}
            value={last4}
            onChange={(e) => setLast4(e.target.value)}
          />
        ) : null}
        <div className="form-grid">
          <InputText
            label="Opening balance"
            description="From a statement. A card, line of credit or loan you owe on is a minus amount."
            prefix="$"
            inputMode="decimal"
            value={opening}
            onChange={(e) => setOpening(e.target.value)}
          />
          <InputText
            label="On the day"
            description="The statement's last day. Reconcile starts here."
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
